/**
 * Parking marketplace (/parking/marketplace) — V0.3 marquee Tier 1.
 *
 * Owner-to-resident rental marketplace. Four tabs:
 *   Listings           — table of active + paused listings.
 *   Pending approvals  — admin approves listings, picks the using_unit.
 *   Active rentals     — allocations where allocation_kind = owner_rental.
 *   Settings           — read-only summary linking to /settings parking-policy.
 *
 * Backend contracts:
 *   GET  /v1/parking/marketplace/listings?status=…
 *   POST /v1/parking/marketplace/listings/:id/approve   body { using_unit_id }
 *   POST /v1/parking/marketplace/listings/:id/reject
 *   POST /v1/parking/marketplace/listings/:id/cancel
 *   GET  /v1/parking/allocations?allocation_kind=owner_rental&active=true
 *   GET  /v1/society/me                                  (for policy display)
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  ArrowLeft,
  ArrowLeftRight,
  CheckCircle2,
  XCircle,
  Building2,
  IndianRupee,
  Settings as SettingsIcon
} from 'lucide-react';
import { toast } from 'sonner';
import { get, post, getErrorMessage } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TabsPills } from '@/components/ui/tabs-pills';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { CursorPaginator } from '@/components/ui/cursor-paginator';
import { useCursorAccumulator } from '@/hooks/useCursorAccumulator';
import { formatDate } from '@/lib/format';
import type {
  MarketplaceListing,
  MarketplaceEnquiry,
  ParkingAllocation,
  UnitLite,
  CursorList,
  SocietyParkingPolicy
} from './_parking-types';
import {
  MARKETPLACE_STATUS_BADGE,
  ENQUIRY_STATUS_BADGE,
  ENQUIRY_STATUS_LABEL
} from './_parking-types';

const STATUS_LABEL: Record<MarketplaceListing['status'], string> = {
  pending: 'Pending approval',
  approved: 'Approved',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
  taken: 'Taken'
};

function formatInr(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return `₹${n.toLocaleString('en-IN')}`;
}

type Tab = 'listings' | 'pending' | 'enquiries' | 'active' | 'settings';

const ApproveSchema = z.object({
  using_unit_id: z.string().min(1, 'Unit required')
});
type ApproveForm = z.infer<typeof ApproveSchema>;

export function ParkingMarketplacePage(): JSX.Element {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('listings');

  // ─── Listings (all-statuses tab) — uses status filter.
  const [listingsStatus, setListingsStatus] = useState<MarketplaceListing['status'] | 'all'>('approved');
  const filtersHash = useMemo(() => JSON.stringify([listingsStatus]), [listingsStatus]);
  const [cursor, setCursor] = useState<string | null>(null);
  useEffect(() => {
    setCursor(null);
  }, [filtersHash]);

  const listingsQ = useQuery({
    queryKey: ['parking-marketplace-listings', listingsStatus, cursor],
    queryFn: () =>
      get<CursorList<MarketplaceListing>>('/parking/marketplace/listings', {
        params: {
          limit: 50,
          ...(listingsStatus !== 'all' ? { status: listingsStatus } : {}),
          ...(cursor ? { cursor } : {})
        }
      }),
    enabled: tab === 'listings'
  });
  const listingsPag = useCursorAccumulator<MarketplaceListing>(listingsQ.data, cursor, filtersHash);
  const listings = listingsPag.items;

  // ─── Pending approvals (status=active means awaiting tenant; some servers use
  // a 'pending' status — we accept both via the dedicated endpoint).
  const pendingQ = useQuery({
    queryKey: ['parking-marketplace-listings', 'pending'],
    queryFn: () =>
      get<CursorList<MarketplaceListing>>('/parking/marketplace/listings', {
        params: { status: 'pending', limit: 100 }
      }),
    enabled: tab === 'pending'
  });
  const pending = pendingQ.data?.items ?? [];

  // ─── Active rentals = allocations with allocation_kind = owner_rental.
  const activeRentalsQ = useQuery({
    queryKey: ['parking-allocations', 'owner-rental-active'],
    queryFn: () =>
      get<CursorList<ParkingAllocation>>('/parking/allocations', {
        params: { allocation_kind: 'owner_rental', active: true, limit: 200 }
      }),
    enabled: tab === 'active'
  });
  const activeRentals = activeRentalsQ.data?.items ?? [];

  // ─── Society policy (settings tab read-only summary).
  const policyQ = useQuery({
    queryKey: ['society', 'me'],
    queryFn: () => get<SocietyParkingPolicy>('/society/me'),
    enabled: tab === 'settings'
  });

  // ─── Enquiries (resident applies for listed slots; admin matches/declines).
  const [enquiriesStatus, setEnquiriesStatus] = useState<'pending' | 'all'>('pending');
  const enquiriesQ = useQuery({
    queryKey: ['parking-marketplace-enquiries', enquiriesStatus],
    queryFn: () =>
      get<CursorList<MarketplaceEnquiry>>('/parking/marketplace/enquiries', {
        params: {
          limit: 100,
          ...(enquiriesStatus !== 'all' ? { status: enquiriesStatus } : {})
        }
      }),
    enabled: tab === 'enquiries'
  });
  const enquiries = enquiriesQ.data?.items ?? [];

  // Cheap header count: peek a separate query so the Enquiries tab pill
  // always shows pending volume regardless of which sub-filter is active.
  const enquiriesPendingCountQ = useQuery({
    queryKey: ['parking-marketplace-enquiries', 'pending-count'],
    queryFn: () =>
      get<CursorList<MarketplaceEnquiry>>('/parking/marketplace/enquiries', {
        params: { status: 'pending', limit: 100 }
      })
  });
  const enquiriesPendingCount = enquiriesPendingCountQ.data?.items?.length ?? 0;

  // ─── Units list for the approve dialog picker.
  const unitsQ = useQuery({
    queryKey: ['units-lite'],
    queryFn: () => get<{ items: UnitLite[] }>('/society/units', { params: { limit: 1000 } }),
    staleTime: 5 * 60 * 1000
  });

  // Approve / reject / cancel dialogs.
  const [approveTarget, setApproveTarget] = useState<MarketplaceListing | null>(null);
  const approveForm = useForm<ApproveForm>({
    resolver: zodResolver(ApproveSchema),
    mode: 'onChange',
    defaultValues: { using_unit_id: '' }
  });
  useEffect(() => {
    if (approveTarget) approveForm.reset({ using_unit_id: '' });
  }, [approveTarget]); // eslint-disable-line react-hooks/exhaustive-deps

  const approve = useMutation({
    mutationFn: (v: ApproveForm) => {
      if (!approveTarget) throw new Error('No listing');
      return post<MarketplaceListing>(
        `/parking/marketplace/listings/${approveTarget.id}/approve`,
        { using_unit_id: v.using_unit_id }
      );
    },
    onSuccess: () => {
      toast.success('Listing approved');
      setApproveTarget(null);
      void queryClient.invalidateQueries({ queryKey: ['parking-marketplace-listings'] });
      void queryClient.invalidateQueries({ queryKey: ['parking-allocations'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const [rejectTarget, setRejectTarget] = useState<MarketplaceListing | null>(null);
  const reject = useMutation({
    mutationFn: (id: string) => post<MarketplaceListing>(`/parking/marketplace/listings/${id}/reject`, {}),
    onSuccess: () => {
      toast.success('Listing rejected');
      setRejectTarget(null);
      void queryClient.invalidateQueries({ queryKey: ['parking-marketplace-listings'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const [cancelTarget, setCancelTarget] = useState<MarketplaceListing | null>(null);
  const cancel = useMutation({
    mutationFn: (id: string) => post<MarketplaceListing>(`/parking/marketplace/listings/${id}/cancel`, {}),
    onSuccess: () => {
      toast.success('Listing cancelled');
      setCancelTarget(null);
      void queryClient.invalidateQueries({ queryKey: ['parking-marketplace-listings'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  // ─── Enquiry actions ─────────────────────────────────────────────────────
  const matchEnquiry = useMutation({
    mutationFn: ({ id, notes }: { id: string; notes?: string }) =>
      post<{ enquiry: MarketplaceEnquiry; allocation: ParkingAllocation }>(
        `/parking/marketplace/enquiries/${id}/match`,
        notes ? { notes } : {}
      ),
    onSuccess: () => {
      toast.success('Enquirer matched — allocation created');
      void queryClient.invalidateQueries({ queryKey: ['parking-marketplace-enquiries'] });
      void queryClient.invalidateQueries({ queryKey: ['parking-marketplace-listings'] });
      void queryClient.invalidateQueries({ queryKey: ['parking-allocations'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const [declineTarget, setDeclineTarget] = useState<MarketplaceEnquiry | null>(null);
  const [declineNotes, setDeclineNotes] = useState('');
  const declineEnquiry = useMutation({
    mutationFn: ({ id, notes }: { id: string; notes: string }) =>
      post<MarketplaceEnquiry>(`/parking/marketplace/enquiries/${id}/decline`, { notes }),
    onSuccess: () => {
      toast.success('Enquiry declined');
      setDeclineTarget(null);
      setDeclineNotes('');
      void queryClient.invalidateQueries({ queryKey: ['parking-marketplace-enquiries'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  return (
    <>
      <PageHeader
        title="Parking marketplace"
        description="Owner-to-resident slot rentals. Approve listings, monitor active rentals, manage policy."
        actions={
          <Button variant="ghost" size="sm" onClick={() => navigate({ to: '/parking' })} title="Back to parking">
            <ArrowLeft className="h-4 w-4" /> Back
          </Button>
        }
      />

      <div className="mx-auto w-full max-w-[1400px] space-y-vt-4 px-vt-6 pb-vt-8 pt-vt-4">
        <Card>
          <CardContent className="flex items-center gap-vt-2 p-vt-3">
            <TabsPills
              value={tab}
              onChange={(v) => setTab(v as Tab)}
              options={[
                { value: 'listings', label: 'Listings' },
                { value: 'pending', label: 'Pending', count: pending.length || undefined },
                {
                  value: 'enquiries',
                  label: 'Enquiries',
                  count: enquiriesPendingCount || undefined
                },
                { value: 'active', label: 'Active rentals', count: activeRentals.length || undefined },
                { value: 'settings', label: 'Settings' }
              ]}
              aria-label="Marketplace sections"
            />
          </CardContent>
        </Card>

        {/* Listings tab */}
        {tab === 'listings' ? (
          <Card>
            <CardContent className="p-vt-4">
              <div className="mb-vt-3 flex flex-wrap items-center gap-vt-3">
                <TabsPills
                  size="sm"
                  value={listingsStatus}
                  onChange={(v) => setListingsStatus(v as MarketplaceListing['status'] | 'all')}
                  options={[
                    { value: 'pending', label: 'Pending approval' },
                    { value: 'approved', label: 'Approved' },
                    { value: 'rejected', label: 'Rejected' },
                    { value: 'cancelled', label: 'Cancelled' },
                    { value: 'taken', label: 'Taken' },
                    { value: 'all', label: 'All' }
                  ]}
                  aria-label="Listing status filter"
                />
              </div>
              {listingsQ.isLoading ? (
                <div className="flex flex-col gap-vt-2">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : listingsQ.isError ? (
                <EmptyState
                  icon={<ArrowLeftRight className="h-6 w-6" />}
                  title="Could not load listings"
                  description={getErrorMessage(listingsQ.error)}
                />
              ) : listings.length === 0 ? (
                <EmptyState
                  icon={<ArrowLeftRight className="h-6 w-6" />}
                  title="No listings in this status"
                  description="Owners can list their unused slots from the resident app."
                />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Slot</TableHead>
                      <TableHead>Listed by</TableHead>
                      <TableHead className="text-right">Rent</TableHead>
                      <TableHead className="text-right">Notice (days)</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {listings.map((l) => (
                      <TableRow key={l.id}>
                        <TableCell className="font-mono text-vt-13 font-medium text-vt-text">
                          {l.slot?.slot_number ?? '—'}
                          {l.slot?.zone?.name ? (
                            <span className="ml-vt-1 text-vt-12 text-vt-text-muted">
                              · {l.slot.zone.name}
                            </span>
                          ) : null}
                        </TableCell>
                        <TableCell className="text-vt-13 text-vt-text-muted">
                          {l.listed_by_unit
                            ? `${l.listed_by_unit.block?.name ? l.listed_by_unit.block.name + ' · ' : ''}${l.listed_by_unit.number}`
                            : '—'}
                        </TableCell>
                        <TableCell className="text-right font-mono text-vt-13 text-vt-text">
                          {formatInr(l.monthly_rent_inr)}
                        </TableCell>
                        <TableCell className="text-right text-vt-text-muted">
                          {l.notice_period_days}
                        </TableCell>
                        <TableCell>
                          <Badge variant={MARKETPLACE_STATUS_BADGE[l.status]}>
                            {STATUS_LABEL[l.status]}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-vt-12 text-vt-text-muted">
                          {formatDate(l.created_at)}
                        </TableCell>
                        <TableCell className="text-right">
                          {l.status === 'pending' || l.status === 'approved' ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              title="Cancel listing"
                              onClick={() => setCancelTarget(l)}
                            >
                              <XCircle className="h-4 w-4" /> Cancel
                            </Button>
                          ) : (
                            <span className="text-vt-12 text-vt-text-muted">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              <CursorPaginator
                hasMore={Boolean(listingsPag.nextCursor)}
                loading={listingsQ.isFetching}
                itemsShown={listings.length}
                pageSize={50}
                onLoadMore={() => {
                  if (listingsPag.nextCursor) setCursor(listingsPag.nextCursor);
                }}
              />
            </CardContent>
          </Card>
        ) : null}

        {/* Pending approvals */}
        {tab === 'pending' ? (
          <div className="space-y-vt-3">
            {pendingQ.isLoading ? (
              <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-32 w-full" />
                ))}
              </div>
            ) : pending.length === 0 ? (
              <Card>
                <CardContent>
                  <EmptyState
                    icon={<ArrowLeftRight className="h-6 w-6" />}
                    title="Nothing pending"
                    description="New marketplace listings awaiting admin approval will show here."
                  />
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
                {pending.map((l) => (
                  <Card key={l.id}>
                    <CardContent className="flex flex-col gap-vt-3 p-vt-4">
                      <div className="flex items-start justify-between gap-vt-3">
                        <div className="min-w-0">
                          <div className="text-vt-12 text-vt-text-muted">
                            Listed by{' '}
                            <span className="text-vt-text">
                              {l.listed_by_unit
                                ? `${l.listed_by_unit.block?.name ? l.listed_by_unit.block.name + ' · ' : ''}${l.listed_by_unit.number}`
                                : '—'}
                            </span>
                          </div>
                          <h4 className="mt-vt-1 text-vt-14 font-semibold text-vt-text">
                            Slot {l.slot?.slot_number ?? '—'}
                            {l.slot?.zone?.name ? ` · ${l.slot.zone.name}` : ''}
                          </h4>
                          <div className="mt-vt-1 flex flex-wrap items-center gap-vt-2 text-vt-12 text-vt-text-muted">
                            <span>
                              <IndianRupee className="mr-vt-1 inline h-3 w-3" />
                              {formatInr(l.monthly_rent_inr)}/month
                            </span>
                            <span>· Notice {l.notice_period_days} days</span>
                          </div>
                        </div>
                        <Badge variant="info">Pending</Badge>
                      </div>
                      {l.notes ? (
                        <p className="text-vt-12 text-vt-text-muted">{l.notes}</p>
                      ) : null}
                      <div className="flex flex-wrap items-center justify-end gap-vt-2 border-t border-vt-border pt-vt-2">
                        <Button size="sm" variant="secondary" onClick={() => setRejectTarget(l)}>
                          <XCircle className="h-4 w-4" /> Reject
                        </Button>
                        <Button size="sm" onClick={() => setApproveTarget(l)}>
                          <CheckCircle2 className="h-4 w-4" /> Approve
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        ) : null}

        {/* Enquiries */}
        {tab === 'enquiries' ? (
          <Card>
            <CardContent className="p-vt-4">
              <div className="mb-vt-3 flex flex-wrap items-center gap-vt-3">
                <TabsPills
                  size="sm"
                  value={enquiriesStatus}
                  onChange={(v) => setEnquiriesStatus(v as 'pending' | 'all')}
                  options={[
                    { value: 'pending', label: 'Pending' },
                    { value: 'all', label: 'All' }
                  ]}
                  aria-label="Enquiry status filter"
                />
              </div>
              {enquiriesQ.isLoading ? (
                <div className="flex flex-col gap-vt-2">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : enquiriesQ.isError ? (
                <EmptyState
                  icon={<ArrowLeftRight className="h-6 w-6" />}
                  title="Could not load enquiries"
                  description={getErrorMessage(enquiriesQ.error)}
                />
              ) : enquiries.length === 0 ? (
                <EmptyState
                  icon={<ArrowLeftRight className="h-6 w-6" />}
                  title="No enquiries"
                  description="Residents enquire about owner-listed slots from the resident app."
                />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Listing</TableHead>
                      <TableHead>Enquirer unit</TableHead>
                      <TableHead>Vehicle</TableHead>
                      <TableHead>Notes</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {enquiries.map((e) => (
                      <TableRow key={e.id}>
                        <TableCell className="font-mono text-vt-13 text-vt-text-muted">
                          {e.listing_id.slice(0, 8)}…
                        </TableCell>
                        <TableCell className="text-vt-13 text-vt-text-muted">
                          {e.enquirer_unit_id.slice(0, 8)}…
                        </TableCell>
                        <TableCell className="text-vt-13 text-vt-text-muted">
                          {e.vehicle_id ? `${e.vehicle_id.slice(0, 8)}…` : '—'}
                        </TableCell>
                        <TableCell className="max-w-[240px] truncate text-vt-12 text-vt-text-muted">
                          {e.notes ?? '—'}
                        </TableCell>
                        <TableCell className="text-vt-12 text-vt-text-muted">
                          {formatDate(e.created_at)}
                        </TableCell>
                        <TableCell>
                          <Badge variant={ENQUIRY_STATUS_BADGE[e.status]}>
                            {ENQUIRY_STATUS_LABEL[e.status]}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {e.status === 'pending' ? (
                            <div className="flex items-center justify-end gap-vt-1">
                              <Button
                                size="sm"
                                variant="secondary"
                                title="Decline enquiry"
                                onClick={() => {
                                  setDeclineTarget(e);
                                  setDeclineNotes('');
                                }}
                              >
                                <XCircle className="h-4 w-4" /> Decline
                              </Button>
                              <Button
                                size="sm"
                                title="Match enquirer to listing"
                                loading={matchEnquiry.isPending}
                                onClick={() => matchEnquiry.mutate({ id: e.id })}
                              >
                                <CheckCircle2 className="h-4 w-4" /> Match
                              </Button>
                            </div>
                          ) : (
                            <span className="text-vt-12 text-vt-text-muted">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        ) : null}

        {/* Active rentals */}
        {tab === 'active' ? (
          <Card>
            <CardContent className="p-vt-4">
              {activeRentalsQ.isLoading ? (
                <div className="flex flex-col gap-vt-2">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : activeRentals.length === 0 ? (
                <EmptyState
                  icon={<Building2 className="h-6 w-6" />}
                  title="No active rentals"
                  description="Owner-rental allocations appear here as soon as listings are approved."
                />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Slot</TableHead>
                      <TableHead>Renter unit</TableHead>
                      <TableHead className="text-right">Rent</TableHead>
                      <TableHead className="text-right">Society cut</TableHead>
                      <TableHead>Period</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {activeRentals.map((a) => (
                      <TableRow key={a.id}>
                        <TableCell className="font-mono text-vt-13 font-medium text-vt-text">
                          {a.slot?.slot_number ?? '—'}
                          {a.slot?.zone?.name ? (
                            <span className="ml-vt-1 text-vt-12 text-vt-text-muted">
                              · {a.slot.zone.name}
                            </span>
                          ) : null}
                        </TableCell>
                        <TableCell className="text-vt-13 text-vt-text-muted">
                          {a.using_unit
                            ? `${a.using_unit.block?.name ? a.using_unit.block.name + ' · ' : ''}${a.using_unit.number}`
                            : '—'}
                        </TableCell>
                        <TableCell className="text-right font-mono text-vt-13 text-vt-text">
                          {formatInr(a.monthly_rent_inr)}
                        </TableCell>
                        <TableCell className="text-right text-vt-text-muted">
                          {a.society_cut_pct !== null && a.society_cut_pct !== undefined
                            ? `${a.society_cut_pct}%`
                            : '—'}
                        </TableCell>
                        <TableCell className="text-vt-12 text-vt-text-muted">
                          {a.rental_start ? formatDate(a.rental_start) : '—'}
                          {a.rental_end ? ` → ${formatDate(a.rental_end)}` : ''}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        ) : null}

        {/* Settings (read-only) */}
        {tab === 'settings' ? (
          <Card>
            <CardContent className="p-vt-5">
              <div className="flex items-start justify-between gap-vt-3">
                <div>
                  <h3 className="text-vt-16 font-semibold text-vt-text">Marketplace policy</h3>
                  <p className="text-vt-12 text-vt-text-muted">
                    These values are set in Settings → Parking policy and apply to every approval.
                  </p>
                </div>
                <Button size="sm" variant="secondary" asChild>
                  <Link to="/settings">
                    <SettingsIcon className="h-4 w-4" /> Open settings
                  </Link>
                </Button>
              </div>
              {policyQ.isLoading ? (
                <div className="mt-vt-4 grid grid-cols-1 gap-vt-3 md:grid-cols-3">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : policyQ.data ? (
                <dl className="mt-vt-4 grid grid-cols-1 gap-vt-3 md:grid-cols-3">
                  <PolicyField label="Allow owner rentals" value={policyQ.data.parking_allow_owner_rental ? 'Yes' : 'No'} />
                  <PolicyField label="Society cut" value={`${policyQ.data.parking_society_cut_pct ?? 0}%`} />
                  <PolicyField label="Notice period" value={`${policyQ.data.parking_rental_notice_days ?? 0} days`} />
                </dl>
              ) : null}
            </CardContent>
          </Card>
        ) : null}
      </div>

      {/* Approve dialog */}
      <Dialog open={Boolean(approveTarget)} onOpenChange={(v) => (v ? null : setApproveTarget(null))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve listing</DialogTitle>
            <DialogDescription>
              Pick the unit that will use this slot. The allocation is created with{' '}
              <span className="font-mono">owner_rental</span> kind.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={approveForm.handleSubmit((v) => approve.mutate(v))}
            className="flex flex-col gap-vt-3"
          >
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="ap-unit">Using unit</Label>
              <Select id="ap-unit" {...approveForm.register('using_unit_id')}>
                <option value="">— Pick unit —</option>
                {(unitsQ.data?.items ?? []).map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.block ? `${u.block.name} · ` : ''}
                    {u.number}
                  </option>
                ))}
              </Select>
              {approveForm.formState.errors.using_unit_id ? (
                <p className="text-vt-12 text-vt-alert">{approveForm.formState.errors.using_unit_id.message}</p>
              ) : null}
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setApproveTarget(null)}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={approve.isPending}
                loadingText="Saving…"
                disabled={approve.isPending || !approveForm.formState.isValid}
              >
                <CheckCircle2 className="h-4 w-4" /> Approve
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={Boolean(rejectTarget)}
        onOpenChange={(v) => (v ? null : setRejectTarget(null))}
        title="Reject listing"
        description={
          rejectTarget?.slot
            ? `Reject the listing for slot ${rejectTarget.slot.slot_number}? The owner can re-list later.`
            : null
        }
        confirmLabel="Reject"
        variant="warn"
        loading={reject.isPending}
        onConfirm={() => {
          if (rejectTarget) reject.mutate(rejectTarget.id);
        }}
      />

      <ConfirmDialog
        open={Boolean(cancelTarget)}
        onOpenChange={(v) => (v ? null : setCancelTarget(null))}
        title="Cancel listing"
        description={
          cancelTarget?.slot
            ? `Cancel the listing for slot ${cancelTarget.slot.slot_number}? This removes it from the marketplace.`
            : null
        }
        confirmLabel="Cancel listing"
        variant="danger"
        loading={cancel.isPending}
        onConfirm={() => {
          if (cancelTarget) cancel.mutate(cancelTarget.id);
        }}
      />

      {/* Decline enquiry dialog */}
      <Dialog
        open={Boolean(declineTarget)}
        onOpenChange={(v) => {
          if (!v) {
            setDeclineTarget(null);
            setDeclineNotes('');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Decline enquiry</DialogTitle>
            <DialogDescription>
              Share a brief reason — the resident sees this in their app.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="decline-notes">Reason</Label>
            <Textarea
              id="decline-notes"
              value={declineNotes}
              onChange={(e) => setDeclineNotes(e.target.value)}
              placeholder="Slot taken / size mismatch / waiting list etc."
              maxLength={500}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setDeclineTarget(null);
                setDeclineNotes('');
              }}
            >
              Cancel
            </Button>
            <Button
              loading={declineEnquiry.isPending}
              loadingText="Declining…"
              disabled={declineEnquiry.isPending || declineNotes.trim().length === 0}
              onClick={() => {
                if (declineTarget) {
                  declineEnquiry.mutate({ id: declineTarget.id, notes: declineNotes.trim() });
                }
              }}
            >
              <XCircle className="h-4 w-4" /> Decline
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function PolicyField({ label, value }: { label: string; value: React.ReactNode }): JSX.Element {
  return (
    <div className="rounded-vt-sm bg-vt-surface-2/40 px-vt-3 py-vt-2">
      <dt className="text-[10px] uppercase tracking-[0.08em] text-vt-text-soft">{label}</dt>
      <dd className="mt-vt-1 text-vt-14 font-medium text-vt-text">{value}</dd>
    </div>
  );
}
